import { z } from 'zod';
/**
 * Ghi chú phát hành của một bản (tag) — APM-GIT-l, docs/plan-git-integration.md §8.5.
 *
 * Tệp riêng (không nối vào `git.ts` / `git-release.ts`) để các task `APM-GIT-*` chạy
 * song song không giẫm lên nhau. Ba phần:
 *
 * - **Hình API** của bốn route ở `git-release-notes.controller.ts`.
 * - **Output** của skill `draft_release_notes` (luật #6: chỉ là gợi ý, người duyệt).
 * - **Hàm thuần** `releaseNoteMarkdown` — đổi output của skill thành thân ghi chú
 *   (markdown). API dùng nó khi đưa gợi ý vào bảng `git_release_note`; không có
 *   chuỗi hiển thị cố định nào ở đây (tiêu đề nhóm do model viết, mã việc do hệ
 *   thống) — bản mẫu làm tay dựng ở web, qua i18n.
 */
/** Nguồn của chữ: AI gợi ý (chờ duyệt) · đã duyệt · viết tay. Cùng enum `GitTextStatus` của DB. */
export const GitTextStatus = z.enum(['ai_suggested', 'approved', 'manual']);
/** Trần thân một ghi chú (ký tự). Cột là `MEDIUMTEXT`; trần này là để đọc được, không phải để vừa cột. */
export const GIT_RELEASE_NOTE_MAX_CHARS = 20_000;
export const GitReleaseNoteView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    tag: z.string(),
    /** Markdown. Có thể mang tiêu đề MR / lời commit đã qua tay model hay người — hiển thị qua bộ lọc markdown. */
    body: z.string(),
    status: GitTextStatus,
    /** Lượt chạy AI đã gợi ý bản này; `null` = viết tay từ đầu. */
    aiRunId: z.uuid().nullable(),
    approvedBy: z.uuid().nullable(),
    approvedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
});
/**
 * `GET …/git/releases/:releaseId/note?asOf=` — `note: null` là "chưa có ghi chú" (một
 * giá trị, không phải body rỗng — bẫy "endpoint trả null trần"). Với `asOf` quá khứ,
 * `note` dựng lại từ event `git_release_note.*` (mỗi event mang trọn state).
 */
export const GitReleaseNoteState = z.object({
    asOf: z.iso.datetime().nullable(),
    note: GitReleaseNoteView.nullable(),
});
/** `PUT …/note` — viết tay (hoặc sửa bản AI / bản mẫu) ⇒ `manual`. */
export const WriteGitReleaseNoteInput = z.object({
    body: z.string().trim().min(1).max(GIT_RELEASE_NOTE_MAX_CHARS),
});
/** `POST …/note/from-suggestion` — đưa gợi ý `draft_release_notes` vào bảng ở `ai_suggested`. */
export const ImportGitReleaseNoteInput = z.object({ suggestionId: z.uuid() });
/** Kết quả đưa gợi ý vào: `applied = false` khi bản đang có đã duyệt / viết tay (AI không đè người). */
export const ImportGitReleaseNoteResult = z.object({
    applied: z.boolean(),
    note: GitReleaseNoteView.nullable(),
});
// ── Output của skill `draft_release_notes` ──────────────────────────────────
/**
 * Một dòng ghi chú: lời cho người không đọc code, kèm id các việc nó nói tới.
 * `itemIds` là id task / issue lấy **nguyên** từ `list_release_items` — grounding
 * đối chiếu chúng, và `finalize` loại id không thuộc bản.
 */
export const DraftReleaseNoteEntry = z.object({
    text: z.string().trim().min(1).max(500),
    itemIds: z.array(z.uuid()).min(1).max(30),
});
export const DraftReleaseNoteSection = z.object({
    /** "Tính năng mới", "Sửa lỗi"… — model đặt theo nội dung, không theo loại việc thô. */
    title: z.string().trim().min(1).max(80),
    entries: z.array(DraftReleaseNoteEntry).min(1).max(60),
});
export const DraftReleaseNotesOutput = z.object({
    /** 1–3 câu mở đầu: bản này mang lại gì cho người dùng. */
    summary: z.string().trim().min(1).max(1000),
    sections: z.array(DraftReleaseNoteSection).max(12),
});
/**
 * Output của skill ⇒ thân ghi chú (markdown). `codeOf(id)` trả mã việc (`GOLD-12`)
 * hoặc `null` (việc đã xoá) — mã đứng cuối dòng trong ngoặc, để người đọc lần về việc.
 *
 * Chữ của model đi qua `inline()`: một dòng, bỏ ký tự đầu dòng markdown có thể biến
 * lời ghi chú thành tiêu đề / khối mã. Không phải lớp bảo vệ XSS — web hiển thị qua
 * `MarkdownContent` như mọi văn bản không tin cậy.
 */
export function releaseNoteMarkdown(output, codeOf) {
    const lines = [inline(output.summary)];
    for (const section of output.sections) {
        lines.push('', `## ${inline(section.title)}`, '');
        for (const entry of section.entries) {
            const codes = [...new Set(entry.itemIds.map(codeOf).filter((c) => c !== null))];
            lines.push(`- ${inline(entry.text)}${codes.length > 0 ? ` (${codes.join(', ')})` : ''}`);
        }
    }
    return lines.join('\n').trim();
}
function inline(text) {
    return text
        .replace(/\s+/g, ' ')
        .replace(/^[#>\-*+`\s]+/, '')
        .trim();
}
//# sourceMappingURL=git-release-note.js.map